import React, { useContext, useEffect, useRef, useState } from "react";
import { PERSONAL_MESSAGE_MAX_LENGTH } from "../../../../constants/global";
import { useDebounce } from "../../../../debounce";
import { hasXSSCharacters } from "../../../../helpers/Utils";
import IGiftCardGif from "../../../../models/GiftCardGif.model";
import { CustomiseMailStepModel, ICustomiseMailStepModel } from "../../../../models/buy-giftcard/customise-card/CustomiseMailStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { IBuyGiftCardStepProps } from "../../../../models/interfaces/BuyGiftCardStep.interface";
import DataService from "../../../../services/DataService.service";
import { AppSettingContext } from "../../../Application";
import LoadingComponent from "../../../shared/LoadingComponent";
import MediaUploader from "../../shared/MediaUploader";

interface ICustomiseMailStepProps extends IBuyGiftCardStepProps {
    stepModel: ICustomiseMailStepModel;
    isAvailable: boolean;

    handlePersonalMessageChange(personalMessage: string): void;
    handleMediaChange(mediaUrl: string): void;
    handleGoBack(): void;
    handleSubmit(stepModel: ICustomiseMailStepModel): void;
}

const CustomiseMailStep = (props: ICustomiseMailStepProps) => {
    const { labels } = useContext(AppSettingContext);
    const checkboxRef = useRef(null);
    const culture = document.documentElement.lang;

    //#region STATE PROPERTIES

    const [stepModel, setStepModel] = useState<ICustomiseMailStepModel>({
        writePersonalMessage: props.stepModel?.writePersonalMessage,
        personalMessage: props.stepModel?.personalMessage ?? "",
        addMedia: props.stepModel?.addMedia,
        useGif: props.stepModel?.useGif,
        mediaUrl: props.stepModel?.mediaUrl,
        userMedias: props.stepModel?.userMedias
    });
    const [isModelValid, setIsModelValid] = useState<boolean>();
    const [lastUploadedTemplate, setLastUploadedTemplate] = useState("");
    const [gifs, setGifs] = useState(Array<IGiftCardGif>(0));
    const [isLoading, setIsLoading] = useState(true);
    const [areGifsLoading, setAreGifsLoading] = useState<boolean>();

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if ((stepModel.mediaUrl && !stepModel.useGif) || (stepModel.mediaUrl && !stepModel.mediaUrl.includes("giphy.com"))) {
            setLastUploadedTemplate(stepModel.mediaUrl);
            setStepModel({
                ...stepModel,
                addMedia: true,
                useGif: false
            });

            props.handleMediaChange(stepModel.mediaUrl);
        } else if ((stepModel.mediaUrl && stepModel.useGif) || (stepModel.mediaUrl && stepModel.mediaUrl.includes("giphy.com"))) {
            setStepModel({
                ...stepModel,
                addMedia: true,
                useGif: true
            });
        }
    }, []);

    useEffect(() => {
        setIsModelValid(isValid());
    }, [stepModel]);

    useEffect(() => {
        setAreGifsLoading(false);

        setTimeout(() => {
            initSlider();
        }, 600);
    }, [gifs]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    useEffect(() => {
        checkboxRef.current.checked = props.activeStep === BuyGiftCardStateEnum.CUSTOMISE_MAIL;

        setTimeout(() => {
            if (stepModel.addMedia && stepModel.useGif) {
                loadAllGifs("");
            }
        }, 200);
    }, [props.activeStep]);

    //#endregion

    //#region HANDLERS

    const handleExpandBoxCheck = (): void => {
        if (props.isAvailable) {
            if (checkboxRef.current.checked && props.activeStep !== BuyGiftCardStateEnum.CUSTOMISE_MAIL) {
                props.handleSetActiveStep(BuyGiftCardStateEnum.CUSTOMISE_MAIL);

                setTimeout(() => {
                    if (stepModel.addMedia && stepModel.useGif) {
                        loadAllGifs("");
                    }
                }, 200);
            }
        }
    };

    const handleWritePersonalMessageSwitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setStepModel({
            ...stepModel,
            writePersonalMessage: event.target.checked
        });

        if (event.target.checked) {
            props.handlePersonalMessageChange(stepModel.personalMessage);
        } else {
            props.handlePersonalMessageChange("");
        }
    };

    const handlePersonalMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        if (hasXSSCharacters(event.target.value)) {
            event.preventDefault();
        } else {
            setStepModel({
                ...stepModel,
                personalMessage: event.target.value
            });

            props.handlePersonalMessageChange(event.target.value);
        }
    };

    const handleAddMediaSwitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setStepModel({
            ...stepModel,
            addMedia: event.target.checked
        });

        if (event.target.checked) {
            props.handleMediaChange(stepModel.mediaUrl);
            loadAllGifs("");
        } else {
            props.handleMediaChange("");
        }
    };

    const handleUseGifChange = (useGif: boolean): void => {
        if (useGif) {
            loadAllGifs("");
        }

        setStepModel({
            ...stepModel,
            useGif: useGif
        });
    };

    const handleGifSearch = useDebounce<React.ChangeEvent<HTMLInputElement>>((event) => {
        loadAllGifs(event.target.value);
    }, 1000);

    const handleMediaChange = (mediaUrl: string): void => {
        // deselect if already selected
        if (mediaUrl === stepModel.mediaUrl) {
            mediaUrl = "";
        }

        setStepModel({
            ...stepModel,
            addMedia: true,
            mediaUrl: mediaUrl
        });

        props.handleMediaChange(mediaUrl);
    };

    const handleGoBack = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();
        props.handleGoBack();
    };

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        if (!isValid()) {
            return;
        }

        props.handleSubmit(stepModel);
        checkboxRef.current.checked = false;
    };

    //#endregion

    //#region COMMON HELPERS

    const loadAllGifs = (searchTerm: string): void => {
        setAreGifsLoading(true);

        DataService.getGiftCardsGifsTemplates(culture, searchTerm)
            .then(response => {
                if (response) {
                    const gifItems = Array<IGiftCardGif>(0);
                    response.forEach((item, index) => {
                        gifItems.push(item);
                    });

                    setGifs(gifItems);
                }
            }).catch(error => {
                console.error(error);
            });
    };

    const initSlider = (): void => {
        if (document.querySelector('.swiper-gif')) {
            // @ts-ignore
            const swiperGif = new Swiper('.swiper-gif', {
                slidesPerView: "auto",
                spaceBetween: 0,
                slidesPerGroup: 2,
                grid: {
                    rows: 2,
                },
                freeMode: true,
                navigation: {
                    nextEl: '.swiper-gif-button-next',
                    prevEl: '.swiper-gif-button-prev',
                },
            });
        }
    };

    const isValid = (): boolean => {
        return CustomiseMailStepModel.isValid(stepModel);
    };

    //#endregion

    return (
        <div className="mail-customization box">
            <input
                type="checkbox"
                name="mail-customization"
                id="mail-customization"
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={false}
            />
            <div className="action-card">
                <h4 className="box-heading">
                    <i className="box-icon">
                        <img src="/static/options-card-page/options-card-icon-05.png" alt="icon 05" />
                    </i>
                    {labels.get("GiftCard.App.V2.CustomiseMail.Title")}
                    <i className="box-arrow"
                        style={{ cursor: props.isAvailable ? "pointer" : "default" }}
                        onClick={() => {
                            if (props.isAvailable) {
                                checkboxRef.current.checked = !checkboxRef.current.checked;
                                handleExpandBoxCheck();
                            }
                        }}
                    ></i>
                </h4>
                {props.activeStep === BuyGiftCardStateEnum.CUSTOMISE_MAIL && (
                    <div className="show">
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div className="slide-content">
                                <div className="personal-message">
                                    <div className="message-details" style={{ margin: '0' }}>
                                        {/*<h5>{labels.get("GiftCard.App.V2.CustomiseMail.Subtitle")}</h5>*/}
                                        <input
                                            type="checkbox"
                                            name="personal-message__switch"
                                            id="personal-message__switch"
                                            className="switch-input"
                                            checked={stepModel.writePersonalMessage}
                                            onChange={handleWritePersonalMessageSwitch}
                                        />
                                        <div className="switch-label-box">
                                            <p>{labels.get("GiftCard.App.V2.CustomiseMail.Label.PersonalMessage")}</p>
                                            <label htmlFor="personal-message__switch" className="switch">
                                                <span className="switch__slider switch__round"></span>
                                            </label>
                                        </div>
                                        <div className="tab">
                                            <div className="slide-content">
                                                <label htmlFor="personal__message"></label>
                                                <div className="msg">
                                                    <textarea
                                                        name="personal__message"
                                                        id="personal__message"
                                                        maxLength={PERSONAL_MESSAGE_MAX_LENGTH}
                                                        placeholder={labels.get("GiftCard.App.V2.CustomiseMail.Placeholder.PersonalMessage")}
                                                        value={stepModel.personalMessage}
                                                        onChange={handlePersonalMessageChange}
                                                    ></textarea>
                                                </div>
                                                <div
                                                    className="remaining-chars"
                                                    style={{ color: `${PERSONAL_MESSAGE_MAX_LENGTH - stepModel.personalMessage.length <= 20 ? "red" : ""}` }}
                                                >
                                                    {PERSONAL_MESSAGE_MAX_LENGTH - stepModel.personalMessage.length}/{PERSONAL_MESSAGE_MAX_LENGTH}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="add-multimedia add-multimedia-gif">
                                        <input
                                            type="checkbox"
                                            name="add-multimedia__switch-gif"
                                            id="add-multimedia__switch-gif"
                                            className="switch-input"
                                            checked={stepModel.addMedia}
                                            onChange={handleAddMediaSwitch}
                                        />
                                        <div className="switch-label-box">
                                            <p>Add a Photo, Video or GIF</p>
                                            <label htmlFor="add-multimedia__switch-gif" className="switch">
                                                <span className="switch__slider switch__round"></span>
                                            </label>
                                        </div>

                                        {stepModel.addMedia && (
                                            <>
                                                <ul className="card-cover">
                                                    <li>
                                                        <input
                                                            type="radio"
                                                            name="card-cover"
                                                            id="card-template"
                                                            checked={stepModel.useGif}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleUseGifChange(true);
                                                                }
                                                            }}
                                                        />
                                                        <label htmlFor="card-cover">
                                                            {labels.get("GiftCard.App.V2.CustomiseMail.Button.Gif")}
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <input
                                                            type="radio"
                                                            name="card-cover"
                                                            id="upload-photo"
                                                            checked={!stepModel.useGif}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleUseGifChange(false);
                                                                }
                                                            }}
                                                        />
                                                        <label htmlFor="upload-photo">
                                                            {labels.get("GiftCard.App.V2.CustomiseMail.Button.Upload")}
                                                        </label>
                                                    </li>
                                                </ul>

                                                {stepModel.useGif ? (
                                                    <div className="card-templates fadeIn" style={{ display: 'grid' }}>
                                                        <div className="tab" style={{ display: 'grid', gridTemplateRows: '1fr', overflow: 'hidden', transition: 'grid-template-rows .5s', gridColumn: 'span 2', marginBottom: '20px' }}>
                                                            <div className="slide-content">
                                                                <input type="text"
                                                                    name="add-multimedia__search"
                                                                    onChange={handleGifSearch}
                                                                    placeholder={labels.get("GiftCard.App.Steps.Customise.SearchGifs.Placeholder")}
                                                                    className="search-input" />

                                                                <div className="gif-container">
                                                                    <div className="swiper-gif">
                                                                        {areGifsLoading ? (
                                                                            <LoadingComponent />
                                                                        ) : (
                                                                            <div className="swiper-wrapper" style={{ flexDirection: 'column' }}>
                                                                                {gifs.map((gif, index) => (
                                                                                    <div className="swiper-slide" key={index} >
                                                                                        <span
                                                                                            className={(stepModel.mediaUrl && (stepModel.mediaUrl === gif.video || stepModel.mediaUrl === gif.image)) ? "selected" : ""}
                                                                                            style={{ height: 'inherit' }}
                                                                                            onClick={() => handleMediaChange(gif.video.trim() ? gif.video : gif.image)}
                                                                                        >
                                                                                            <img
                                                                                                style={{ height: 'inherit' }}
                                                                                                alt={gif.title}
                                                                                                src={gif.video.trim() ? gif.video : gif.image}
                                                                                            />
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <div className="swiper-gif-button-prev small-fs">
                                                                        <svg width="7" height="13" viewBox="0 0 7 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M6 11.5L1 6.5L6 1.5" stroke="#9363D9" strokeWidth="1.67" strokeLinecap="round"
                                                                                strokeLinejoin="round" />
                                                                        </svg>
                                                                        {labels.get("GiftCard.App.Steps.Customise.Templates.Btn.Previous")}
                                                                    </div>
                                                                    <div className="swiper-gif-button-next small-fs">
                                                                        {labels.get("GiftCard.App.Steps.Customise.Templates.Btn.Next")}
                                                                        <svg width="7" height="13" viewBox="0 0 7 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M1 1.5L6 6.5L1 11.5" stroke="#9363D9" strokeWidth="1.67" strokeLinecap="round"
                                                                                strokeLinejoin="round" />
                                                                        </svg>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <MediaUploader
                                                        uploaderId={2}
                                                        selectedMediaUrl={stepModel.mediaUrl}
                                                        lastUploadedTemplate={lastUploadedTemplate}
                                                        isVideoAllowed={true}
                                                        imageCropperAspectRatio={NaN}

                                                        setLastUploadedTemplate={setLastUploadedTemplate}
                                                        handleMediaChange={handleMediaChange}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="buttons">
                                    <button
                                        className="btn btn__full_width btn__secondary large-fs btn__large"
                                        onClick={handleGoBack}
                                    >
                                        {labels.get("GiftCard.App.V2.CustomiseMail.Button.GoBack")}
                                    </button>
                                    <button
                                        className="btn btn__full_width btn__primary large-fs btn__large"
                                        onClick={handleSaveAndContinue}
                                        disabled={!isModelValid}
                                    >
                                        {labels.get("GiftCard.App.V2.CustomiseMail.Button.Submit")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomiseMailStep;